import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Button,
  LinearProgress,
} from '@mui/material';
import {
  Pets as PetsIcon,
  Egg as EggIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useDashboardOverview,
  useDashboardMetrics,
  useDashboardAlerts,
} from '../hooks/useDashboard';
import QuickPricingRecommendation from '../components/QuickPricingRecommendation';

// ── Shared chart style helpers ────────────────────────────────────────────────
const chartAxis = { fill: 'rgba(255,255,255,0.5)', fontSize: 12 };
const chartAxisLine = { stroke: 'rgba(255,255,255,0.08)' };
const chartGrid = { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.06)' };
const chartTooltipStyle = {
  backgroundColor: '#1a1a1a',
  border: '1px solid rgba(255,152,0,0.3)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 13,
};
const chartLegend = { color: 'rgba(255,255,255,0.6)', fontSize: 13 };

// ── Stat Card ─────────────────────────────────────────────────────────────────
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  iconBg: string;
  subtitle?: string;
  accent?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, iconBg, subtitle, accent }) => (
  <Paper
    sx={{
      p: 3,
      height: '100%',
      background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
      border: `1px solid ${accent || 'rgba(255,152,0,0.2)'}`,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '2px',
        background: accent || 'linear-gradient(90deg, #ff9800, #ffa726)',
      },
    }}
  >
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ letterSpacing: '0.6px', textTransform: 'uppercase', color: 'rgba(255,152,0,0.8)' }}
        >
          {title}
        </Typography>
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{ mt: 1.5, mb: 0.5, color: '#ffffff', fontSize: 26, letterSpacing: '-0.02em' }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          bgcolor: iconBg,
          borderRadius: 2.5,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

// ── Chart wrapper ─────────────────────────────────────────────────────────────
const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper
    sx={{
      p: 3,
      height: '100%',
      background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
      border: '1px solid rgba(255,152,0,0.15)',
    }}
  >
    <Typography
      variant="subtitle1"
      fontWeight={700}
      sx={{ mb: 2.5, color: '#ffffff', letterSpacing: '-0.01em' }}
    >
      {title}
    </Typography>
    {children}
  </Paper>
);

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const {
    data: overview,
    isLoading: overviewLoading,
    isFetching: overviewFetching,
    error: overviewError,
    refetch: refetchOverview,
  } = useDashboardOverview();
  const {
    data: metrics,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
    error: metricsError,
    refetch: refetchMetrics,
  } = useDashboardMetrics();
  const {
    data: alerts,
    isLoading: alertsLoading,
    isFetching: alertsFetching,
    error: alertsError,
    refetch: refetchAlerts,
  } = useDashboardAlerts();

  const isLoading = overviewLoading || metricsLoading || alertsLoading;
  const hasError = overviewError || metricsError || alertsError;
  const isFetching = overviewFetching || metricsFetching || alertsFetching;

  const handleRetry = () => {
    if (overviewError) refetchOverview();
    if (metricsError) refetchMetrics();
    if (alertsError) refetchAlerts();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#ff9800' }} />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth="500px">
          <Alert
            severity="error"
            sx={{
              mb: 3,
              background: 'rgba(211,47,47,0.1)',
              border: '1px solid rgba(211,47,47,0.3)',
              color: '#ff8a80',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Failed to Load Dashboard Data
            </Typography>
            <Typography variant="body2">
              {overviewError instanceof Error
                ? overviewError.message
                : metricsError instanceof Error
                  ? metricsError.message
                  : alertsError instanceof Error
                    ? alertsError.message
                    : 'An error occurred while fetching dashboard data.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={handleRetry}
            startIcon={<RefreshIcon />}
            sx={{ color: '#000' }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!overview) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Alert severity="warning">No dashboard data available</Alert>
      </Box>
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': {
              background: 'linear-gradient(90deg, #ff9800 0%, #ffa726 100%)',
            },
          }}
        />
      )}

      {/* ── Page header ── */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ color: '#ffffff', letterSpacing: '-0.02em' }}
        >
          {greeting} 👋
        </Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
          Here&apos;s what&apos;s happening on the farm today.
        </Typography>
      </Box>

      {/* ── Stat Cards ── */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Birds"
            value={overview?.farm_overview?.total_birds || '0'}
            subtitle={`${overview.farm_overview.laying_birds} laying (${overview?.farm_overview?.laying_percentage?.toFixed(1) || 0}%)`}
            icon={<PetsIcon sx={{ color: '#ff9800', fontSize: 22 }} />}
            iconBg="rgba(255,152,0,0.15)"
            accent="rgba(255,152,0,0.35)"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Monthly Production"
            value={overview?.production_overview?.current_month_eggs || '0'}
            subtitle="crates of eggs this month"
            icon={<EggIcon sx={{ color: '#4caf50', fontSize: 22 }} />}
            iconBg="rgba(76,175,80,0.15)"
            accent="rgba(76,175,80,0.35)"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Revenue"
            value={`GH₵${(Number(overview.financial_overview.total_revenue) / 100).toLocaleString()}`}
            icon={<MoneyIcon sx={{ color: '#2196f3', fontSize: 22 }} />}
            iconBg="rgba(33,150,243,0.15)"
            accent="rgba(33,150,243,0.35)"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Net Profit"
            value={`GH₵${(Number(overview.financial_overview.net_profit) / 100).toLocaleString()}`}
            subtitle={`Expenses: GH₵${(Number(overview.financial_overview.total_expenses) / 100).toLocaleString()}`}
            icon={<TrendingUpIcon sx={{ color: '#ab47bc', fontSize: 22 }} />}
            iconBg="rgba(171,71,188,0.15)"
            accent="rgba(171,71,188,0.35)"
          />
        </Grid>
      </Grid>

      {/* ── Alerts ── */}
      {alerts && alerts.alerts && alerts.alerts.length > 0 && (
        <Paper
          sx={{
            p: 3,
            mb: 4,
            border: '1px solid rgba(255,152,0,0.25)',
            background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, #ff9800, #ffa726)',
            },
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
            <Box display="flex" alignItems="center" gap={1}>
              <WarningIcon sx={{ color: '#ff9800', fontSize: 20 }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: '#ffffff' }}>
                Alerts & Notifications
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip
                label={`${alerts.total_alerts} Total`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
              />
              {alerts.critical_count > 0 && (
                <Chip
                  label={`${alerts.critical_count} Critical`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(211,47,47,0.2)',
                    color: '#ff6b6b',
                    border: '1px solid rgba(211,47,47,0.3)',
                  }}
                />
              )}
              {alerts.warning_count > 0 && (
                <Chip
                  label={`${alerts.warning_count} Warning`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255,152,0,0.15)',
                    color: '#ff9800',
                    border: '1px solid rgba(255,152,0,0.3)',
                  }}
                />
              )}
            </Box>
          </Box>
          <Grid container spacing={2}>
            {alerts.alerts.map((alert: any, index: number) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${alert.type}-${index}`}>
                <Alert
                  severity={alert.level === 'critical' ? 'error' : alert.level}
                  sx={{
                    height: '100%',
                    background:
                      alert.level === 'critical'
                        ? 'rgba(211,47,47,0.1)'
                        : alert.level === 'warning'
                          ? 'rgba(255,152,0,0.1)'
                          : 'rgba(33,150,243,0.1)',
                    border:
                      alert.level === 'critical'
                        ? '1px solid rgba(211,47,47,0.3)'
                        : alert.level === 'warning'
                          ? '1px solid rgba(255,152,0,0.3)'
                          : '1px solid rgba(33,150,243,0.3)',
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    {alert.title}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    {alert.message}
                  </Typography>
                  {alert.count > 0 && (
                    <Chip label={`${alert.count} items`} size="small" sx={{ mt: 1, height: 20 }} />
                  )}
                </Alert>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── Charts ── */}
      <Grid container spacing={3}>
        {/* Pricing widget */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <QuickPricingRecommendation />
        </Grid>

        {/* Egg Production Trend */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <ChartCard title="Egg Production Trend">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={metrics?.production_trends || []}>
                <CartesianGrid {...chartGrid} />
                <XAxis
                  dataKey="period"
                  tick={chartAxis}
                  axisLine={chartAxisLine}
                  tickLine={false}
                />
                <YAxis tick={chartAxis} axisLine={chartAxisLine} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={chartLegend} />
                <Line
                  type="monotone"
                  dataKey="total_eggs"
                  stroke="#ff9800"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#ff9800', strokeWidth: 0 }}
                  activeDot={{ r: 6, fill: '#ff9800' }}
                  name="Eggs Produced"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Sales Trend */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title="Sales Revenue Trend">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={metrics?.sales_trends || []}>
                <CartesianGrid {...chartGrid} />
                <XAxis
                  dataKey="period"
                  tick={chartAxis}
                  axisLine={chartAxisLine}
                  tickLine={false}
                />
                <YAxis tick={chartAxis} axisLine={chartAxisLine} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={chartLegend} />
                <Bar
                  dataKey="total_revenue"
                  fill="#ff9800"
                  radius={[4, 4, 0, 0]}
                  name="Revenue (GH₵)"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Mortality Trend */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title="Mortality Trend">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={metrics?.mortality_trends || []}>
                <CartesianGrid {...chartGrid} />
                <XAxis
                  dataKey="period"
                  tick={chartAxis}
                  axisLine={chartAxisLine}
                  tickLine={false}
                />
                <YAxis tick={chartAxis} axisLine={chartAxisLine} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={chartLegend} />
                <Line
                  type="monotone"
                  dataKey="total_deaths"
                  stroke="#ef5350"
                  strokeWidth={2.5}
                  dot={{ r: 4, fill: '#ef5350', strokeWidth: 0 }}
                  activeDot={{ r: 6 }}
                  name="Mortalities"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Expenditure Trend */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <ChartCard title="Expenditure Trend">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={metrics?.expenditure_trends || []}>
                <CartesianGrid {...chartGrid} />
                <XAxis
                  dataKey="period"
                  tick={chartAxis}
                  axisLine={chartAxisLine}
                  tickLine={false}
                />
                <YAxis tick={chartAxis} axisLine={chartAxisLine} tickLine={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend wrapperStyle={chartLegend} />
                <Bar
                  dataKey="total_amount"
                  fill="#ab47bc"
                  radius={[4, 4, 0, 0]}
                  name="Expenses (GH₵)"
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>

      {/* ── Quick Stats ── */}
      <Paper
        sx={{
          p: 3,
          mt: 3,
          background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
          border: '1px solid rgba(255,152,0,0.15)',
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#ffffff', mb: 2 }}>
          Quick Stats
        </Typography>
        <Box display="flex" gap={1.5} flexWrap="wrap">
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
            label={`${overview?.assigned_requests?.filter((r: any) => r.status === 'pending').length || 0} Pending Requests`}
            sx={{
              bgcolor: overview?.assigned_requests?.filter((r: any) => r.status === 'pending')
                .length
                ? 'rgba(33,150,243,0.15)'
                : 'rgba(255,255,255,0.06)',
              color: overview?.assigned_requests?.filter((r: any) => r.status === 'pending').length
                ? '#42a5f5'
                : 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<WarningIcon sx={{ fontSize: 16 }} />}
            label={`${overview?.health_overview?.current_month_mortalities || 0} Mortalities This Month`}
            sx={{
              bgcolor:
                Number(overview?.health_overview?.current_month_mortalities || 0) > 0
                  ? 'rgba(255,152,0,0.15)'
                  : 'rgba(255,255,255,0.06)',
              color:
                Number(overview?.health_overview?.current_month_mortalities || 0) > 0
                  ? '#ff9800'
                  : 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<PeopleIcon sx={{ fontSize: 16 }} />}
            label={`${overview?.farm_overview?.active_customers || 0} Active Customers`}
            sx={{
              bgcolor: 'rgba(76,175,80,0.15)',
              color: '#66bb6a',
              border: '1px solid rgba(76,175,80,0.2)',
              fontWeight: 600,
            }}
          />
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
            label={`Cycle: ${overview?.financial_cycle?.cycle_number || 'N/A'}`}
            sx={{
              bgcolor: overview?.financial_cycle?.is_closing_soon
                ? 'rgba(255,152,0,0.15)'
                : 'rgba(255,255,255,0.06)',
              color: overview?.financial_cycle?.is_closing_soon
                ? '#ff9800'
                : 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              fontWeight: 600,
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default Dashboard;
